import { RouteData, Destination, getRouteImageUrl, RouteImageFiles, RouteArray, Coordinate } from "../util/route-data";
import { getById } from "../util/dom";
import { distanceFromCoords } from "../util/gps";

// In Miles. Only save route points that are at least this many miles apart.
export const MIN_ROUTE_DISTANCE_PRECISION = 0.08; 

export function setStatus(msg: string) {
  const li = document.createElement('li');
  li.classList.add('import-line');
  li.textContent = msg;
  getById('import-status').prepend(li);
}

export async function textFileToString(file: File|ArrayBuffer|NonSharedBuffer, firstNBytes?: number): Promise<string> {
  if (!(file instanceof File)) {
    const decoder = new TextDecoder();
    return decoder.decode(firstNBytes ? file.slice(0, firstNBytes) : file);
  }
  const reader = new FileReader();
  reader.readAsText(file, 'utf-8');
  return new Promise<string>((resolve, reject) => {
    reader.addEventListener('load', () => {
      const result = reader.result as string;
      if (firstNBytes) {
        resolve(result.slice(0, firstNBytes));
      } else {
        resolve(reader.result as string);
      }
    });
    reader.addEventListener('error', () => reject(reader.error));
  });
}

export function convertToRouteArray(coords: Coordinate[]): { route: RouteArray, totalDistance: number } {
  let totalDistance = 0;
  let prevSavedDistance = 0;
  const route = [coords[0].lat, coords[0].lon, 0];
  for (let i = 0; i < coords.length - 1; i++) {
    const coord1 = coords[i];
    const coord2 = coords[i+1];
    totalDistance += distanceFromCoords(coord1, coord2);

    if (totalDistance - prevSavedDistance >= MIN_ROUTE_DISTANCE_PRECISION ||
        i === coords.length - 2) {
      route.push(coord2.lat, coord2.lon, totalDistance);
      prevSavedDistance = totalDistance;
    }
  }
  return {route, totalDistance};
}

export function metersToMiles(meters: number): number {
  return meters * 0.000621371;
}

export function sortSpots(route: RouteData) {
  route.spots.sort(function(a, b) {
    const val = getSortValue(a.mile, b.mile);
    if (val !== 0) {
      return val;
    }
    return getSortValue(a.name, b.name);
  });
}

function getSortValue<T>(a: T, b: T): number {
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
}

export async function requestPersistedStorage() {
  if (navigator.storage && navigator.storage.persist &&
      !(await navigator.storage.persisted())) {
    setStatus('Asking for persisted storage. This will ensure your route is not automatically ' +
              'deleted by the browser.')
    await navigator.storage.persist();
  }
}

export async function saveImages(images: RouteImageFiles): Promise<void> {
  navigator.serviceWorker.controller!.postMessage({
    command: 'clearImageCache'
  });
  for (const [imagePath, fileBuffer] of Object.entries(images)) {
    navigator.serviceWorker.controller!.postMessage(
      {
        'command': 'saveImage',
        'path': getRouteImageUrl(imagePath),
        'fileContent': fileBuffer
    }, [fileBuffer]);
  }
}


const URL_REGEX = /[-a-zA-Z0-9@:%_\+.~#?&//=]{2,256}\.[a-z]{2,4}\b(\/[-a-zA-Z0-9@:%_\+.~#?&//=]*)?/gi;

export function textToHtml(text: string): string {
  return htmlEncode(text).replace(URL_REGEX,
      (url: string) => '<a href="' + url + '" target="_blank">website</a>');
}

function htmlEncode(val: string): string {
  const el = document.createElement('div');
  el.textContent = val;
  // Unescape the new lines.
  return el.innerHTML.replaceAll('\n', '<br>').replace(/&lt;br&gt;/g, '<br>');
}